/**
 * The boot run: one workspace boot, the stage it is in, and the tasks that hold it there.
 *
 * Two axes describe a boot and are kept apart. The *kind* says which calls run and how: a cold boot pages the whole
 * workspace, a warm one sends cursors. The *stage* says where the boot is: `bootstrapping` while the blocking calls
 * run, `warming` while the calls that bring the store up to speed run without blocking, and `ready` after, when every
 * load is business as usual and nothing shows for it. The rail indicator reads the stage; the manifest below is the one
 * place that says which tasks a stage waits for.
 *
 * A stage is complete when every task named for it has settled or been skipped, so a declared task holds its stage
 * from the start of the run, whether or not it has started: a task behind a gate that has not opened yet still holds
 * it. There is no quiet timer. A name the manifest does not declare, or a declared name run a second time, holds the
 * run out of `ready` only while it runs, and only when it starts before `ready`; after that it passes through. One run
 * at a time: a new run replaces the current one, and a task counted in a replaced run settles with no effect.
 */

import { Duration } from "luxon";

import { recordEvent } from "@/services/telemetry";

/** The stages of a boot, in order. The run's stage is the first one that is not complete. */
export const BOOT_STAGES = ["bootstrapping", "warming", "ready"] as const;
export type BootStage = (typeof BOOT_STAGES)[number];

/** A stage that tasks are declared for. */
export type BootWorkStage = Exclude<BootStage, "ready">;

/**
 * Which kind of boot this is, which decides which calls run and how:
 * - `cold` — a full paged bootstrap, with no prior bootstrap for the workspace.
 * - `warm` — the first boot of this page load against an already bootstrapped workspace.
 * - `incremental` — any later bootstrap run in the same page load: a workspace switch, a re-boot.
 */
export type BootKind = "cold" | "warm" | "incremental";

/** Why the boot started: a workspace boot, or the divergence probe re-running the bootstrap. */
export type BootTrigger = "boot" | "rebootstrap";

/** The tasks each stage waits for, by name. */
export type BootManifest = Readonly<Record<BootWorkStage, readonly string[]>>;

/** What a workspace boot waits for: the bootstrap, then every call that warms the store. */
export const BOOT_MANIFEST: BootManifest = {
  bootstrapping: ["bootstrap"],
  warming: [
    "bootstrap.postpass",
    "bootstrap.integrity",
    "feedWarmup",
    "driverTranscripts",
    "invitations",
    "tmsEvents",
    "currentRoutes",
    "acknowledgements",
  ],
};

/** What a re-bootstrap waits for: the bootstrap and its own follow-ups; the warmup calls never re-run. */
export const REBOOTSTRAP_MANIFEST: BootManifest = {
  bootstrapping: ["bootstrap"],
  warming: ["bootstrap.postpass", "bootstrap.integrity"],
};

/** Where a declared task stands: not yet started, running, settled (resolved or rejected), or skipped by a gate. */
export type BootTaskState = "pending" | "running" | "settled" | "skipped";

/** What the UI and tests read. A new object on every change, so `useSyncExternalStore` sees it. */
export type BootRunView = {
  stage: BootStage;
  trigger: BootTrigger;
  /** Undefined until the bootstrap has decided full vs incremental. */
  kind: BootKind | undefined;
  tasks: Readonly<Record<string, BootTaskState>>;
};

/** How long a run may sit in `warming` before it is reported as stalled, once, with the tasks still pending. */
export const BOOT_STALL_MS = Duration.fromObject({ minutes: 5 }).toMillis();

/** Which stage work that the manifest did not declare holds the run at: the last one before `ready`. */
const EXTRA_WORK_STAGE: BootWorkStage = "warming";

type Task = { stage: BootWorkStage; state: BootTaskState };

type Run = {
  trigger: BootTrigger;
  kind: BootKind | undefined;
  tasks: Map<string, Task>;
  /** Undeclared or repeated tasks running; they hold the run out of `ready` while they do. */
  extra: number;
  stallTimer: ReturnType<typeof setTimeout> | undefined;
  stalled: boolean;
};

const READY_VIEW: BootRunView = { stage: "ready", trigger: "boot", kind: undefined, tasks: {} };

let run: Run | undefined;
let view: BootRunView = READY_VIEW;
const listeners = new Set<() => void>();

/** Starts a run, replacing the current one. Called once per workspace boot, and once per re-bootstrap. */
export function startBootRun({ trigger, manifest }: { trigger: BootTrigger; manifest: BootManifest }): void {
  if (run !== undefined) clearTimeout(run.stallTimer);
  const tasks = new Map<string, Task>();
  for (const stage of BOOT_STAGES) {
    if (stage === "ready") continue;
    for (const name of manifest[stage]) tasks.set(name, { stage, state: "pending" });
  }
  run = { trigger, kind: undefined, tasks, extra: 0, stallTimer: undefined, stalled: false };
  publish();
}

/** Records the run's kind once the bootstrap has decided it. */
export function setBootRunKind(kind: BootKind): void {
  if (run === undefined) return;
  run.kind = kind;
  publish();
}

/**
 * Counts `work` toward `stage` under `name` and returns it unchanged (same resolution or rejection).
 *
 * A declared task that has not started yet is the one this call starts; anything else — no run, a name the manifest
 * does not declare, a declared name run again — counts only while it runs, and only before the run is `ready`.
 *
 * @param stage - The stage the work belongs to.
 * @param name - The task's name; a manifest name when the work is declared.
 * @param work - The promise to count.
 * @returns `work` itself.
 */
export function runBootTask<T>(stage: BootWorkStage, name: string, work: Promise<T>): Promise<T> {
  const current = run;
  if (current === undefined || stageOf(current) === "ready") return work;

  const task = current.tasks.get(name);
  const declared = task !== undefined && task.stage === stage && task.state === "pending";
  if (declared) task.state = "running";
  else current.extra += 1;
  publish();

  const settle = () => {
    // A task of a replaced run settles with no effect on the run that replaced it.
    if (run !== current) return;
    if (declared) task.state = "settled";
    else current.extra -= 1;
    publish();
  };
  work.then(settle, settle);
  return work;
}

/** Marks a declared task that a gate will not run as skipped, so it holds nothing. A no-op once it has started. */
export function skipBootTask(stage: BootWorkStage, name: string): void {
  const task = run?.tasks.get(name);
  if (task === undefined || task.stage !== stage || task.state !== "pending") return;
  task.state = "skipped";
  publish();
}

/** Ends the run early: every task still pending is skipped, so the stage becomes `ready`. For a failed cold boot. */
export function abandonBootRun(): void {
  if (run === undefined) return;
  for (const task of run.tasks.values()) if (task.state === "pending") task.state = "skipped";
  run.extra = 0;
  publish();
}

/** @returns The run as it stands; `ready` with no tasks before any run starts. */
export function getBootRun(): BootRunView {
  return view;
}

/**
 * Subscribes to run changes; listeners fire only when the view changes.
 *
 * @param listener - Called after each change.
 * @returns A function that removes the listener.
 */
export function subscribeBootRun(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Resets all run state. Tests only. */
export function resetBootRunForTests(): void {
  if (run !== undefined) clearTimeout(run.stallTimer);
  run = undefined;
  view = READY_VIEW;
  listeners.clear();
}

function stageOf(current: Run): BootStage {
  for (const stage of BOOT_STAGES) {
    if (stage === "ready") break;
    for (const task of current.tasks.values()) {
      if (task.stage === stage && (task.state === "pending" || task.state === "running")) return stage;
    }
  }
  return current.extra > 0 ? EXTRA_WORK_STAGE : "ready";
}

function pendingNames(current: Run): string[] {
  return [...current.tasks]
    .filter(([, task]) => task.state === "pending" || task.state === "running")
    .map(([name]) => name);
}

/** Arms the stall report while the run is `warming`, and clears it once it leaves. */
function watchForStall(current: Run, stage: BootStage): void {
  if (stage !== "warming") {
    clearTimeout(current.stallTimer);
    current.stallTimer = undefined;
    return;
  }
  if (current.stallTimer !== undefined || current.stalled) return;
  current.stallTimer = setTimeout(() => {
    current.stallTimer = undefined;
    if (run !== current || current.stalled || stageOf(current) !== "warming") return;
    current.stalled = true;
    recordEvent(
      "boot.stalled",
      { boot: { trigger: current.trigger, kind: current.kind ?? "unknown", pending: pendingNames(current) } },
      "reliability",
    );
  }, BOOT_STALL_MS);
}

/** Recomputes the view and notifies listeners when it changed. */
function publish(): void {
  const current = run;
  if (current === undefined) return;
  const stage = stageOf(current);
  watchForStall(current, stage);
  const tasks: Record<string, BootTaskState> = {};
  for (const [name, task] of current.tasks) tasks[name] = task.state;
  const next: BootRunView = { stage, trigger: current.trigger, kind: current.kind, tasks };
  if (sameView(view, next)) return;
  view = next;
  for (const listener of listeners) listener();
}

function sameView(a: BootRunView, b: BootRunView): boolean {
  if (a.stage !== b.stage || a.trigger !== b.trigger || a.kind !== b.kind) return false;
  const names = Object.keys(b.tasks);
  if (names.length !== Object.keys(a.tasks).length) return false;
  return names.every((name) => a.tasks[name] === b.tasks[name]);
}
